import React, { useState } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import breakpoints from "../../../styles/breakpoints";
import { colors, fonts } from "../../../styles/variables";
import tooltipIcon from "../../../assets/images/icons/question_mark_inactive.png";
import tooltipActiveIcon from "../../../assets/images/icons/question_mark_active.png";

const TooltipHolder = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: ${(props) => (props.isSmall ? 20 : 30)}px;
  height: ${(props) => (props.isSmall ? 20 : 30)}px;
  margin-right: ${(props) => (props.addMarginRight ? 25 : 0)}px;
  margin-left: ${(props) => (props.addMarginRight ? 0 : 10)}px;
  border-radius: 50%;
  border: 2px solid ${colors.black};
  background-color: ${colors.black};
  background-image: url("${tooltipIcon}");
  background-repeat: no-repeat;
  background-position: 50% 50%;
  background-size: ${(props) => (props.isSmall ? 10 : 14)}px;
  vertical-align: middle;
  cursor: pointer;
  flex-shrink: 0;
  ${(props) => props.unsetCaps ?? "text-transform: none;"}

  ${(props) =>
    props.showContent &&
    css`
      &:before {
        position: absolute;
        left: ${props.topRight ? "50%" : "auto"};
        right: ${props.topRight ? "auto" : "0"};
        bottom: ${props.isSmall ? 150 : 130}%;
        transform: translateX(${props.topRight ? "-50%" : "0"});
        background-color: ${colors.peach};
        border: 2px solid ${colors.black};
        color: ${colors.black};
        font-family: ${fonts.fontInterstateBlackCompressed};
        content: attr(aria-label);
        padding: 10px;
        width: 160px;
        font-size: 14px;
        line-height: 1.2;
        ${breakpoints.xsDown} {
          width: 120px;
        }
      }
    `};

  &:after {
    position: absolute;
    top: -9px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid ${colors.black};
    content: " ";
    font-size: 0;
    line-height: 0;
    width: 0;
  }

  &:before,
  &:after {
    opacity: 0;
    pointer-events: none;
    text-align: center;
    z-index: 900;
  }

  &:hover,
  &:focus {
    background-color: ${colors.peach};
    background-image: url("${tooltipActiveIcon}");
    &:before,
    &:after {
      opacity: 1;
    }
  }

  ${(props) => props.css}
`;

const OuterContainer = styled.div`
  position: relative;
  font-size: 14px;
`;

const CustomComponentHolder = styled.div`
  position: absolute;
  right: 0;
  bottom: 40px;
  transform: translateX(0);
  background-color: ${colors.peach};
  border: 2px solid ${colors.black};
  color: ${colors.black};
  font-family: ${fonts.fontInterstateBlackCompressed};
  padding: 10px;
  width: 160px;
  font-size: 14px;
  line-height: 1.2;
  ${breakpoints.xsDown} {
    width: 140px;
  }
`;

const Tooltip = ({
  tootipTitle = "Default tooltip",
  topRight = false,
  tooltipStyle = /** @type {string[]} */ ([]),
  customComponent = null,
  isSmall = false,
  addMarginRight = false,
  unsetCaps = false,
}) => {
  const hasCustomComponent = !!customComponent;
  const [showContent, setShowContent] = useState(false);

  if (hasCustomComponent) {
    return (
      <OuterContainer>
        <TooltipHolder
          aria-label=""
          topRight={topRight}
          tabindex="0"
          css={tooltipStyle}
          showContent={false}
          onMouseEnter={() => setShowContent(true)}
          onMouseLeave={() => setShowContent(false)}
          onFocus={() => setShowContent(true)}
          unsetCaps={unsetCaps}
        />
        {showContent && <CustomComponentHolder>{customComponent}</CustomComponentHolder>}
      </OuterContainer>
    );
  }

  return (
    <TooltipHolder
      aria-label={tootipTitle}
      topRight={topRight}
      tabindex="0"
      css={tooltipStyle}
      isSmall={isSmall}
      showContent
      addMarginRight={addMarginRight}
    />
  );
};

Tooltip.propTypes = {
  tootipTitle: PropTypes.string,
  topRight: PropTypes.bool,
  isSmall: PropTypes.bool,
  tooltipStyle: PropTypes.arrayOf(PropTypes.string),
  customComponent: PropTypes.node,
  addMarginRight: PropTypes.bool,
  unsetCaps: PropTypes.bool,
};

export default Tooltip;
